/**
 * Custom hook: fetches per-variant challenger-insights data for a given journey.
 * Self-contained so the builder panel does not depend on the comparisons tab class.
 *
 * CRITICAL: the variant list comes entirely from the API response — we never walk
 * the active graph's split-step branches[]. After a promotion the active revision
 * is linear and has no split branches to walk.
 */

import { useCallback, useEffect, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { InsightsService } from '../../../services/insights'
import { AppState } from '../../../stores/app'
import { Journey } from '../types/journey-api-response'
import { InsightsReportContract } from '../../reporting_v2/helpers/build-api-report-contract'
import { ReportEntity } from '../../reporting_v2/enums/report-entity'
import { ReportDatePreset } from '../../reporting_v2/enums/report-date-preset'
import { ReportDateIncrement } from '../../reporting_v2/enums/report-date-increment'
import {
    VariantApiRow,
    VariantMetricsRow,
    normalizeVariantRow,
} from './split-test-metrics'

export type UseVariantMetricsResult =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'error'; error: string }
    | { status: 'success'; rows: VariantMetricsRow[] }

/**
 * Build the insights package for the SPLIT_TEST_VARIANTS breakdown.
 * Includes purchases/purchase_rate fields — the panel hides those columns
 * when no variant has purchase data (handled in the consumer).
 */
function buildVariantInsightsPackage(domainId: number, journeyId: number): InsightsReportContract {
    return {
        date_preset: ReportDatePreset.LIFETIME,
        entity: ReportEntity.CAMPAIGNS,
        date_increment: ReportDateIncrement.LIFETIME,
        breakdowns: ['campaign', 'variant'],
        fields: [
            'campaign.id',
            'split_test.step_id',
            'split_test.name',
            'variant.id',
            'variant.name',
            'variant.percentage',
            'entrances',
            'impressions',
            'clicks',
            'ctr_decimal',
            'purchases',
            'purchase_amount',
            'purchase_rate',
        ],
        filters: [
            {
                field: 'domain.id',
                operator: 'in',
                value: [domainId],
            },
            {
                field: 'campaign.id',
                operator: 'in',
                value: [journeyId],
            },
        ],
        action_attribution: 'send_time',
    }
}

/**
 * Fetch per-variant metrics for a journey from the challenger insights API.
 *
 * @param journey - the journey object (needs id and domain_id)
 * @param enabled - set to false to skip the fetch (e.g. draft journeys)
 */
export function useVariantMetrics(
    journey: Pick<Journey, 'id' | 'domain_id'> | null | undefined,
    enabled: boolean = true,
    /**
     * Suffix appended to the request cancellation key. Distinct suffixes let
     * two independent consumers (e.g. the shared canvas-chip fetch and the
     * Results panel's own fetch) coexist without cancelling each other.
     */
    cancellationKeySuffix: string = 'default',
): UseVariantMetricsResult {
    const [result, setResult] = useState<UseVariantMetricsResult>({ status: 'idle' })

    const fetchMetrics = useCallback(async () => {
        if (!journey || !enabled) {
            setResult({ status: 'idle' })
            return
        }

        setResult({ status: 'loading' })

        try {
            const insightsSvc = Container.get(InsightsService)
            const appState = Container.get(AppState)

            const domainId = journey.domain_id ?? appState.currentDomain?.id
            if (!domainId) {
                setResult({ status: 'error', error: 'No domain context available.' })
                return
            }

            const pkg = buildVariantInsightsPackage(domainId, journey.id)

            const raw: VariantApiRow[] | undefined = await insightsSvc.fetch(pkg, {
                requestScope: 'domains',
                scopeId: domainId,
                cancellationKey: `split-test-results.${journey.id}.${cancellationKeySuffix}`,
            })

            if (!raw || !Array.isArray(raw)) {
                setResult({ status: 'success', rows: [] })
                return
            }

            const rows: VariantMetricsRow[] = raw
                .filter((stat) => stat.variant?.id != null)
                .map(normalizeVariantRow)

            setResult({ status: 'success', rows })
        } catch (err: unknown) {
            const error = err instanceof Error ? err.message : 'Failed to load variant metrics.'
            setResult({ status: 'error', error })
        }
    }, [journey?.id, journey?.domain_id, enabled, cancellationKeySuffix])

    useEffect(() => {
        fetchMetrics()
    }, [fetchMetrics])

    return result
}
